import { useState, useEffect, useCallback, useRef } from "react";

import { getHeaders } from "../utils/api";

const useApiCallDefault = (
  url,
  method = "GET",
  headers = {},
  body = null,
  navigate = () => {},
  skipErrorInterceptor = false
) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const attemptsRef = useRef(1);
  const maxAttempts = 3;

  const fetchData = useCallback(async () => {
    if (attemptsRef.current > maxAttempts) {
      return;
      navigate("/");
    }

    try {
      const config = {
        method,
        headers: {
          ...headers,
          ...getHeaders(),
          ...(skipErrorInterceptor && { "x-skip-error-interceptor": "true" }),
        },
      };

      if (body !== null) {
        config.body = JSON.stringify(body);
      }

      const response = await fetch(url, config);

      if (!response.ok && skipErrorInterceptor) {
        attemptsRef.current += 1;
        if (attemptsRef.current <= maxAttempts) {
          setTimeout(() => fetchData(), 1000);
          return;
        }
        navigate("/");
        throw new Error(`HTTP error! Status: ${response.status}`);
      } else if (!response.ok) {
        // Let interceptor handle the error, but still retry
        attemptsRef.current += 1;
        if (attemptsRef.current <= maxAttempts) {
          setTimeout(() => fetchData(), 1000);
          return;
        }
      }

      const result = await response.json();
      setData(result);
    } catch (error) {
      setError(error);
      attemptsRef.current += 1;
      setTimeout(async () => {
        await fetchData();
      }, 1000);
    } finally {
      setLoading(false);
    }
  }, [url, method, headers, body, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, error, loading };
};

export default useApiCallDefault;
